import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useInterstitialStore } from '@/store/interstitial/store';
import type { InterstitialTemplate, TemplateData } from '@/store/interstitial/store';
import {
  createStateAvailabilityData,
  getDefaultInterstitialData,
} from '@/components/ui/interstitial/defaultData';
import { useResolvedProductSlug } from '@/hooks/useResolvedProductSlug';
import { getProductGroup } from '@/constants/products';
import { usePersonalizedRecommendationFlow } from '@/hooks/usePersonalizedRecommendationFlow';

const INTERSTITIAL_CONFIG = {
  'circular-progress': { template: 'circular-progress' },
  'circular-progress-with-welcome-steps': { template: 'circular-progress-with-welcome-steps' },
  'welcome-steps': { template: 'welcome-steps' },
  'results-loader': { template: 'results-loader' },
  statistics: { template: 'statistics' },
  'multi-testimonial': { template: 'multi-testimonial' },
  'state-availability': { template: 'state-availability' },
  doctor: { template: 'doctor' },
  'linear-progress': { template: 'linear-progress' },
  'product-info': { template: 'product-info' },
  'performance-chart': { template: 'performance-chart' },
  'product-formulation': { template: 'product-formulation' },
  'sleep-stats': { template: 'sleep-stats' },
} as const;

const CHAIN_CONFIGS = {
  demographics: {
    steps: [
      { type: 'state-availability', flag: 'show_state_availability_interstitial', next: 'doctor' },
      { type: 'doctor', flag: 'show_doctor_interstitial', next: 'FINAL_PATH' },
    ],
    duration: 4000,
  },
  postQuestionnaire: {
    steps: [
      { type: 'linear-progress', flag: 'show_linear_progress_interstitial', next: 'product-info' },
      { type: 'product-info', flag: 'show_product_info_interstitial', next: 'performance-chart' },
      {
        type: 'performance-chart',
        flag: 'show_performance_chart_interstitial',
        next: 'product-formulation',
      },
      {
        type: 'product-formulation',
        flag: 'show_product_formulation_interstitial',
        next: 'FINAL_PATH',
      },
    ],
    duration: 4000,
  },
  postQuestionnairePersonalizedUxFlow: {
    steps: [
      { type: 'results-loader', flag: '', next: 'product-info' },
      { type: 'product-info', flag: 'show_product_info_interstitial', next: 'performance-chart' },
      {
        type: 'performance-chart',
        flag: 'show_performance_chart_interstitial',
        next: 'product-formulation',
      },
      {
        type: 'product-formulation',
        flag: 'show_product_formulation_interstitial',
        next: 'FINAL_PATH',
      },
    ],
    duration: 4000,
  },
} as const;

export const useInterstitialControl = () => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const interstitialStore = useInterstitialStore();
  const productSlug = useResolvedProductSlug();

  const personalizedUxFlowTest = usePersonalizedRecommendationFlow({
    productSlug,
  });
  const navigateToInterstitial = useCallback(
    (template: string) => {
      const hasParams = template.includes('?');
      const subscriptionType = searchParams.get('subscription_type');
      const searchSuffix =
        !hasParams && subscriptionType ? `?subscription_type=${subscriptionType}` : '';
      navigate(`/interstitial/${template}${searchSuffix}`);
      return true;
    },
    [navigate, searchParams],
  );

  const handleInterstitial = useCallback(
    (type: keyof typeof INTERSTITIAL_CONFIG, dataOverride?: Partial<TemplateData>) => {
      const productGroup = getProductGroup(productSlug);
      if (productGroup !== 'rugiet-ed') return false;
      const config = INTERSTITIAL_CONFIG[type];
      const subscriptionType = searchParams.get('subscription_type');
      const currentSearch = subscriptionType ? `?subscription_type=${subscriptionType}` : '';
      const interstitialPath = `/interstitial/${config.template}${currentSearch}`;

      if (dataOverride) {
        interstitialStore.setInterstitial({
          template: config.template as InterstitialTemplate,
          templateData: {
            ...dataOverride,
            type: config.template,
          } as TemplateData,
          nextPath: interstitialPath,
          duration: 4000,
        });
      }
      return navigateToInterstitial(config.template + currentSearch);
    },
    [productSlug, searchParams, navigateToInterstitial, interstitialStore],
  );

  const buildDynamicChain = useCallback(
    (chainType: keyof typeof CHAIN_CONFIGS, finalDestination: string) => {
      const chainConfig = CHAIN_CONFIGS[chainType];
      const enabledSteps: Record<string, string> = {};
      const enabledStepTypes: string[] = [];

      for (const step of chainConfig.steps) {
        enabledStepTypes.push(step.type);
      }

      for (let i = 0; i < enabledStepTypes.length; i++) {
        const currentStepType = enabledStepTypes[i];
        const isLastStep = i === enabledStepTypes.length - 1;

        if (isLastStep) {
          enabledSteps[currentStepType] = finalDestination;
        } else {
          const nextStepType = enabledStepTypes[i + 1];
          enabledSteps[currentStepType] = `/interstitial/${nextStepType}`;
        }
      }

      return enabledSteps;
    },
    [],
  );

  const handleCircularProgress = useCallback(
    (dataOverride?: Partial<TemplateData>) => {
      return handleInterstitial('circular-progress', dataOverride);
    },
    [handleInterstitial],
  );

  const handleStatistics = useCallback(
    (dataOverride?: Partial<TemplateData>) => {
      return handleInterstitial('statistics', dataOverride);
    },
    [handleInterstitial],
  );

  const handleMultiTestimonial = useCallback(
    (dataOverride?: Partial<TemplateData>) => {
      return handleInterstitial('multi-testimonial', dataOverride);
    },
    [handleInterstitial],
  );

  /**
   * Shows the sleep-stats interstitial for Recharge product
   * @param dataOverride - Optional custom template data
   * @returns true if interstitial was shown, false if product check failed
   */
  const handleSleepStats = useCallback(
    (dataOverride?: Partial<TemplateData>) => {
      const productGroup = getProductGroup(productSlug);
      if (productGroup !== 'recharge') return false;

      const config = INTERSTITIAL_CONFIG['sleep-stats'];
      const subscriptionType = searchParams.get('subscription_type');
      const currentSearch = subscriptionType ? `?subscription_type=${subscriptionType}` : '';

      const defaultData = getDefaultInterstitialData('sleep-stats');
      const templateData = dataOverride
        ? ({
            ...defaultData,
            ...dataOverride,
            type: config.template,
          } as TemplateData)
        : defaultData;

      if (templateData) {
        interstitialStore.setInterstitial({
          template: config.template as InterstitialTemplate,
          templateData,
          nextPath: '/signup',
          duration: 4000,
        });
      }

      return navigateToInterstitial(config.template + currentSearch);
    },
    [productSlug, searchParams, navigateToInterstitial, interstitialStore],
  );

  const createChain = useCallback(
    (chainId: string, finalDestination: string, steps: Record<string, string>) => {
      interstitialStore.startChain(chainId, finalDestination, steps);
    },
    [interstitialStore],
  );

  /**
   * Shows the state-availability interstitial chain for Rugiet ED products
   * @param stateName - The state name to display
   * @param finalNextPath - Where to navigate after the interstitial chain completes
   * @returns true if interstitial was shown, false if product check failed or chain is empty
   */
  const handleStateAvailability = useCallback(
    (stateName: string, finalNextPath: string) => {
      const productGroup = getProductGroup(productSlug);
      if (productGroup !== 'rugiet-ed') return false;

      const enabledSteps = buildDynamicChain('demographics', finalNextPath);

      if (Object.keys(enabledSteps).length === 0) {
        return false;
      }

      createChain(`demographics-${Date.now()}`, finalNextPath, enabledSteps);

      const firstStep = Object.keys(enabledSteps)[0];
      const nextStep = enabledSteps[firstStep];

      const templateData = createStateAvailabilityData(stateName);

      interstitialStore.setInterstitial({
        template: 'state-availability',
        templateData,
        nextPath: nextStep,
        duration: CHAIN_CONFIGS.demographics.duration,
      });

      interstitialStore.saveQuestionInterstitial('state-availability-latest', {
        template: 'state-availability',
        templateData,
        nextPath: nextStep,
        duration: CHAIN_CONFIGS.demographics.duration,
      });

      return navigateToInterstitial('state-availability');
    },
    [buildDynamicChain, createChain, interstitialStore, navigateToInterstitial, productSlug],
  );

  const handlePostQuestionnaireChain = useCallback(
    (path: string) => {
      const productGroup = getProductGroup(productSlug);
      if (productGroup !== 'rugiet-ed') return false;
      const enabledSteps = personalizedUxFlowTest
        ? buildDynamicChain('postQuestionnairePersonalizedUxFlow', path)
        : buildDynamicChain('postQuestionnaire', path);

      if (Object.keys(enabledSteps).length === 0) {
        return false;
      }

      const chainId = `post-questionnaire-${Date.now()}`;
      createChain(chainId, path, enabledSteps);

      const firstStep = Object.keys(enabledSteps)[0];
      return navigateToInterstitial(firstStep);
    },
    [buildDynamicChain, createChain, navigateToInterstitial, personalizedUxFlowTest, productSlug],
  );

  const handleSecondStatisticsChain = useCallback(
    (finalDestination: string) => {
      const productGroup = getProductGroup(productSlug);
      if (productGroup !== 'rugiet-ed') return false;

      const chainId = `second-statistics-chain-${Date.now()}`;
      const chainSteps = {
        'statistics-alt': '/interstitial/multi-testimonial',
        'multi-testimonial': finalDestination,
      };

      createChain(chainId, finalDestination, chainSteps);

      const nextStep = chainSteps['statistics-alt'];

      const templateData = {
        type: 'statistics-alt' as const,
        data: {
          headline: '80% of men prefer Rugiet over other ED treatments*',
          imageUrl: '/assets/images/interstitials/statistics-alt.jpg',
          disclaimer:
            '*Based on a survey of 114 active Rugiet Ready patients, conducted in May 2025.',
        },
      };

      interstitialStore.setInterstitial({
        template: 'statistics',
        templateData,
        nextPath: nextStep,
        duration: 4000,
      });

      return navigateToInterstitial('statistics-alt');
    },
    [createChain, interstitialStore, navigateToInterstitial, productSlug],
  );

  const saveCurrentInterstitialForQuestion = useCallback(
    (pathname: string) => {
      const { template, templateData, nextPath, duration } = interstitialStore;
      if (template && templateData) {
        interstitialStore.saveQuestionInterstitial(pathname, {
          template,
          templateData,
          nextPath: nextPath || '',
          duration,
        });
      }
    },
    [interstitialStore],
  );

  return {
    handleCircularProgress,
    handleStatistics,
    handleMultiTestimonial,
    handleSleepStats,
    handleStateAvailability,
    handlePostQuestionnaireChain,
    handleSecondStatisticsChain,
    saveCurrentInterstitialForQuestion,
  };
};
